import { ReactNode } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface KPICardProps {
  title: string;
  value: string | number;
  delta: number;
  deltaLabel?: string;
  icon?: ReactNode;
  format?: 'currency' | 'number' | 'percentage' | 'days';
  invertDelta?: boolean;
  className?: string;
}

export function KPICard({ 
  title, 
  value, 
  delta, 
  deltaLabel = 'vs last period',
  icon,
  format = 'number',
  invertDelta = false,
  className,
}: KPICardProps) {
  const formattedValue = (() => {
    if (typeof value === 'string') return value;
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', { 
          style: 'currency', 
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value);
      case 'percentage':
        return `${value}%`;
      case 'days':
        return `${value} days`;
      default:
        return value.toLocaleString();
    }
  })();

  const isPositive = invertDelta ? delta < 0 : delta > 0;
  const isNegative = invertDelta ? delta > 0 : delta < 0;
  const isNeutral = delta === 0;

  return (
    <div className={cn("kpi-card group", className)}>
      <div className="flex items-start justify-between mb-2 md:mb-3">
        <span className="text-[10px] md:text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {title}
        </span>
        {icon && (
          <div className="text-muted-foreground group-hover:text-primary transition-colors hidden sm:block">
            {icon}
          </div>
        )}
      </div>
      
      <div className="flex items-end justify-between">
        <div>
          <div className="text-lg md:text-2xl font-bold font-mono tracking-tight animate-count-up">
            {formattedValue}
          </div>
          <div className="flex items-center gap-1 md:gap-1.5 mt-1">
            {isPositive && <TrendingUp className="w-3 h-3 text-success" />}
            {isNegative && <TrendingDown className="w-3 h-3 text-destructive" />}
            {isNeutral && <Minus className="w-3 h-3 text-muted-foreground" />}
            <span className={cn(
              "text-[10px] md:text-xs font-medium",
              isPositive && "text-success",
              isNegative && "text-destructive",
              isNeutral && "text-muted-foreground"
            )}>
              {delta > 0 ? '+' : ''}{delta}%
            </span>
            <span className="text-[10px] md:text-xs text-muted-foreground hidden sm:inline">
              {deltaLabel}
            </span>
          </div>
        </div>
        
        {/* Mini sparkline - hidden on mobile */}
        <div className="w-16 h-8 opacity-50 hidden md:block">
          <svg viewBox="0 0 64 32" className="w-full h-full">
            <path
              d="M0,24 L8,20 L16,22 L24,16 L32,18 L40,12 L48,14 L56,8 L64,10"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className={cn(
                isPositive && "text-success",
                isNegative && "text-destructive",
                isNeutral && "text-muted-foreground"
              )}
            />
          </svg>
        </div>
      </div>
    </div>
  );
}
